import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import LoadingCover from '../common/LoadingCover';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetBSCByIdQuery,
  useUpdateBSCMutation,
  useApproveBSCMutation,
  useRejectBSCMutation,
} from '../../redux/balancedScorecardApi';
import { useGetUserByIdQuery } from '../../redux/vmsApi';
import {
  BSCPerspectiveScore,
  BSCKRAScore,
  UNIT_OPTIONS,
  PERFORMANCE_RATINGS,
  calculateKRAScores,
  getGradeFromScore,
  getBonusEntitlementFromScore,
  BalancedScorecard,
} from './bscTypes';

// Helper function to format numbers with thousand separators
const formatNumber = (value: number | undefined): string => {
  if (value === undefined || value === null || isNaN(value)) return '';
  return value.toLocaleString('en-US');
};

// Helper function to parse formatted number string back to number
const parseFormattedNumber = (value: string): number => {
  const cleanValue = value.replace(/,/g, '');
  return parseFloat(cleanValue) || 0;
};

// Helper to get grade color
const getGradeColor = (grade: string): string => {
  const rating = PERFORMANCE_RATINGS.find(r => r.grade === grade);
  return rating?.colorCode || '#9e9e9e';
};

// Helper to get correct bonus entitlement from score (using new bonus thresholds)
const getBonusFromScore = (score: number): number => {
  return getBonusEntitlementFromScore(score);
};

// Formatted Number Input Component
interface FormattedNumberInputProps {
  value: number | undefined;
  onChange: (value: number) => void;
  disabled?: boolean;
  label: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

const FormattedNumberInput: React.FC<FormattedNumberInputProps> = ({
  value,
  onChange,
  disabled = false,
  label,
  startAdornment,
  endAdornment,
}) => {
  const [localValue, setLocalValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value !== undefined && value !== null && !isNaN(value) ? formatNumber(value) : '');
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(value !== undefined && value !== null && !isNaN(value) && value !== 0 ? value.toString() : '');
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsedValue = parseFormattedNumber(localValue);
    onChange(parsedValue);
    setLocalValue(parsedValue ? formatNumber(parsedValue) : '');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const sanitized = inputValue.replace(/[^\d.-]/g, '');
    setLocalValue(sanitized);
    const parsedValue = parseFloat(sanitized) || 0;
    onChange(parsedValue);
  };

  return (
    <TextField
      fullWidth
      label={label}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      size="small"
      inputProps={{ inputMode: 'decimal' }}
      InputProps={{
        startAdornment: startAdornment,
        endAdornment: endAdornment,
      }}
    />
  );
};

// KRA Card Component for Manager Review
interface KRACardProps {
  kra: BSCKRAScore;
  kraIndex: number;
  perspectiveColor: string;
  onUpdateKRA: (field: keyof BSCKRAScore, value: any) => void;
  disabled?: boolean;
}

const KRACard: React.FC<KRACardProps> = ({ kra, kraIndex, perspectiveColor, onUpdateKRA, disabled = false }) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderLeft: `4px solid ${perspectiveColor}`,
        backgroundColor: kra.isExtraDuty ? 'rgba(255, 215, 0, 0.05)' : 'transparent',
      }}
    >
      {/* KRA Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold">
            Goal {kraIndex + 1}
          </Typography>
          {kra.isExtraDuty && (
            <Chip
              size="small"
              icon={<StarIcon sx={{ fontSize: 16 }} />}
              label="Extra Duty"
              color="warning"
              variant="outlined"
            />
          )}
        </Stack>
        <Chip
          label={`Weight: ${kra.weight}%`}
          size="small"
          sx={{ backgroundColor: perspectiveColor, color: 'white' }}
        />
      </Box>

      {/* Goal & Measure */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Goal"
            multiline
            minRows={2}
            maxRows={4}
            value={kra.objective}
            onChange={(e) => onUpdateKRA('objective', e.target.value)}
            placeholder="What do you want to achieve?"
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Measure"
            multiline
            minRows={2}
            maxRows={4}
            value={kra.measure}
            onChange={(e) => onUpdateKRA('measure', e.target.value)}
            placeholder="How will success be measured?"
            disabled={disabled}
          />
        </Grid>
      </Grid>

      {/* Weight, Unit, Direction, Target, Actual */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            fullWidth
            label="Weight"
            type="number"
            value={kra.weight || ''}
            onChange={(e) => onUpdateKRA('weight', parseFloat(e.target.value) || 0)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            size="small"
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>Unit</InputLabel>
            <Select
              value={kra.unitOfMeasure}
              label="Unit"
              onChange={(e: SelectChangeEvent) => onUpdateKRA('unitOfMeasure', e.target.value)}
            >
              {UNIT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>Direction</InputLabel>
            <Select
              value={kra.measurementDirection || 'maximize'}
              label="Direction"
              onChange={(e: SelectChangeEvent) => onUpdateKRA('measurementDirection', e.target.value)}
            >
              <MenuItem value="maximize">↑ Higher</MenuItem>
              <MenuItem value="minimize">↓ Lower</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <FormattedNumberInput
            label="Target"
            value={kra.targetValue}
            onChange={(val) => onUpdateKRA('targetValue', val)}
            startAdornment={kra.unitOfMeasure === 'currency' ? <InputAdornment position="start">$</InputAdornment> : undefined}
            endAdornment={kra.unitOfMeasure === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : undefined}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <FormattedNumberInput
            label="Actual"
            value={kra.actualValue}
            onChange={(val) => onUpdateKRA('actualValue', val)}
            startAdornment={kra.unitOfMeasure === 'currency' ? <InputAdornment position="start">$</InputAdornment> : undefined}
            endAdornment={kra.unitOfMeasure === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : undefined}
            disabled={disabled}
          />
        </Grid>
      </Grid>

      {/* Calculated Results */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          backgroundColor: 'grey.50',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Performance
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: (kra.performance || 0) >= 100 ? 'success.main' : (kra.performance || 0) >= 80 ? 'warning.main' : 'error.main',
            }}
          >
            {(kra.performance || 0).toFixed(1)}%
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Score
          </Typography>
          <Typography variant="h6">{((kra.score || 0) / 100).toFixed(3)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Weighted Score
          </Typography>
          <Typography variant="h6" color="primary">
            {(kra.weightedScore || 0).toFixed(2)}%
          </Typography>
        </Box>
      </Paper>
    </Paper>
  );
};

// Perspective Section Component
interface PerspectiveSectionProps {
  perspective: BSCPerspectiveScore;
  perspectiveIndex: number;
  onUpdatePerspective: (index: number, perspective: BSCPerspectiveScore) => void;
  disabled?: boolean;
}

const PerspectiveSection: React.FC<PerspectiveSectionProps> = ({
  perspective,
  perspectiveIndex,
  onUpdatePerspective,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(true);

  const handleUpdateKRA = (kraIndex: number, field: keyof BSCKRAScore, value: any) => {
    const updatedKRAs = [...perspective.kraScores];
    updatedKRAs[kraIndex] = {
      ...updatedKRAs[kraIndex],
      [field]: value,
    };
    onUpdatePerspective(perspectiveIndex, {
      ...perspective,
      kraScores: updatedKRAs,
    });
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWeight = parseFloat(e.target.value) || 0;
    onUpdatePerspective(perspectiveIndex, {
      ...perspective,
      weight: newWeight,
    });
  };

  const totalKRAWeight = perspective.kraScores.reduce((sum, kra) => sum + (kra.weight || 0), 0);

  const getPerspectiveColor = (name: string) => {
    switch (name) {
      case 'Financial': return '#1976d2';
      case 'Customer': return '#388e3c';
      case 'Internal Processes': return '#f57c00';
      case 'Learning & Growth': return '#7b1fa2';
      default: return '#757575';
    }
  };

  const perspectiveColor = getPerspectiveColor(perspective.name);

  return (
    <Card sx={{ mb: 3, border: `2px solid ${perspectiveColor}` }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: perspectiveColor,
          color: 'white',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography 
            variant="h6" 
            sx={{ color: 'white', cursor: 'pointer' }}
            onClick={() => setExpanded(!expanded)}
          >
            {perspective.name}
          </Typography>
          <TextField
            type="number"
            size="small"
            value={perspective.weight || ''}
            onChange={handleWeightChange}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              sx: {
                backgroundColor: 'white',
                borderRadius: 1,
                '& input': { 
                  width: '50px',
                  textAlign: 'center',
                  py: 0.5,
                },
              },
            }}
            sx={{ width: '100px' }}
          />
          <Chip
            label={`Goal Weight: ${totalKRAWeight}%`}
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.9)', color: perspectiveColor }}
          />
          <Chip
            label={`Total: ${(perspective.perspectiveTotal || 0).toFixed(2)}%`}
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.9)', color: perspectiveColor }}
          />
        </Stack>
        <IconButton sx={{ color: 'white' }} onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <CardContent>
          {perspective.kraScores.length === 0 ? (
            <Alert severity="info">No KRAs defined for this perspective</Alert>
          ) : (
            perspective.kraScores.map((kra, kraIndex) => (
              <KRACard
                key={kraIndex}
                kra={kra}
                kraIndex={kraIndex}
                perspectiveColor={perspectiveColor}
                onUpdateKRA={(field, value) => handleUpdateKRA(kraIndex, field, value)}
                disabled={disabled}
              />
            ))
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

// Main Component
const BSCManagerReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: bscData, isLoading, error, refetch } = useGetBSCByIdQuery({ id: id || '' }, { skip: !id });
  const { data: userData } = useGetUserByIdQuery({ id: bscData?.employeeId || '' }, { skip: !bscData?.employeeId });

  const [updateBSC, { isLoading: isUpdating }] = useUpdateBSCMutation();
  const [approveBSC, { isLoading: isApproving }] = useApproveBSCMutation();
  const [rejectBSC, { isLoading: isRejecting }] = useRejectBSCMutation();

  const [perspectives, setPerspectives] = useState<BSCPerspectiveScore[]>([]);
  const [managerComments, setManagerComments] = useState('');
  const [managerAdjustedScore, setManagerAdjustedScore] = useState<number | undefined>(undefined);
  const [managerAdjustmentNotes, setManagerAdjustmentNotes] = useState('');
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load BSC data into state
  useEffect(() => {
    if (bscData?.perspectives) {
      setPerspectives(bscData.perspectives);
      setManagerComments(bscData.managerComments || '');
      setManagerAdjustedScore(bscData.managerAdjustedScore);
      setManagerAdjustmentNotes(bscData.managerAdjustmentNotes || '');
    }
  }, [bscData]);

  // Calculate scores whenever perspectives change
  const calculatedData = useMemo(() => {
    return calculateKRAScores(perspectives);
  }, [perspectives]);

  const handleUpdatePerspective = useCallback((index: number, updatedPerspective: BSCPerspectiveScore) => {
    setPerspectives(prev => {
      const newPerspectives = [...prev];
      newPerspectives[index] = updatedPerspective;
      return newPerspectives;
    });
    setHasChanges(true);
  }, []);

  // Calculate effective score (use manager override if set)
  const effectiveScore = managerAdjustedScore !== undefined ? managerAdjustedScore : calculatedData.totalWeightedScore;
  const effectiveBonus = getBonusFromScore(effectiveScore);
  const effectiveGrade = getGradeFromScore(effectiveScore)?.grade || 'F';
    
  const handleSaveChanges = async () => {
    if (!id) return;
    
    try {
      await updateBSC({
        id,
        body: {
          perspectives: calculatedData.perspectives,
          totalWeightedScore: effectiveScore,
          finalGrade: effectiveGrade,
          bonusEntitlement: effectiveBonus,
          managerComments,
          managerAdjustedScore: managerAdjustedScore,
          managerAdjustmentNotes: managerAdjustmentNotes,
        },
      }).unwrap();
      setHasChanges(false);
      alert('Changes saved successfully');
      refetch();
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error saving changes');
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    
    try {
      // First save any changes
      if (hasChanges) {
        await updateBSC({
          id,
          body: {
            perspectives: calculatedData.perspectives,
            totalWeightedScore: effectiveScore,
            finalGrade: effectiveGrade,
            bonusEntitlement: effectiveBonus,
            managerAdjustedScore: managerAdjustedScore,
            managerAdjustmentNotes: managerAdjustmentNotes,
          },
        }).unwrap();
      }
      
      // Then approve
      await approveBSC({ id, managerComments }).unwrap();
      setShowApproveDialog(false);
      alert('BSC approved successfully');
      navigate(-1);
    } catch (error) {
      console.error('Error approving BSC:', error);
      alert('Error approving BSC');
    }
  };

  const handleReject = async () => {
    if (!id || !managerComments.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    try {
      await rejectBSC({ id, managerComments }).unwrap();
      setShowRejectDialog(false);
      alert('BSC rejected');
      navigate(-1);
    } catch (error) {
      console.error('Error rejecting BSC:', error);
      alert('Error rejecting BSC');
    }
  };

  if (isLoading) return <LoadingCover />;

  if (error || !bscData) {
    return (
      <PageWithHeader>
        <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">BSC not found</Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mt: 2 }}
            >
              Go Back
            </Button>
          </Paper>
        </Box>
      </PageWithHeader>
    );
  }

  const employeeName = userData?.name || bscData.employeeName || 'Employee';
  const rating = getGradeFromScore(calculatedData.totalWeightedScore);
  const isApproved = bscData.status === 'approved';
  
  // Calculate total perspective weight for validation
  const totalPerspectiveWeight = calculatedData.perspectives.reduce(
    (sum, p) => sum + (p.weight || 0),
    0
  );
  const isWeightValid = totalPerspectiveWeight === 100;

  return (
    <PageWithHeader>
      <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4">BSC Review</Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {employeeName} - {bscData.reviewPeriod}
              </Typography>
            </Box>
            <Chip
              label={bscData.status?.toUpperCase()}
              color={bscData.status === 'approved' ? 'success' : bscData.status === 'submitted' ? 'info' : 'default'}
            />
            {hasChanges && (
              <Chip label="Unsaved Changes" color="warning" variant="outlined" />
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSaveChanges}
              disabled={!hasChanges || isUpdating}
            >
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setShowRejectDialog(true)}
              disabled={bscData.status === 'approved' || isRejecting}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => setShowApproveDialog(true)}
              disabled={bscData.status === 'approved' || isApproving}
            >
              Approve
            </Button>
          </Stack>
        </Box>

        {/* Employee Info Card */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Employee</Typography>
              <Typography fontWeight="bold">{employeeName}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Job Title</Typography>
              <Typography fontWeight="bold">{bscData.jobDescriptionTitle || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Period</Typography>
              <Typography fontWeight="bold">{bscData.reviewPeriod}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Submitted</Typography>
              <Typography fontWeight="bold">
                {bscData.submittedAt ? new Date(bscData.submittedAt).toLocaleDateString() : '-'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Approved Alert */}
        {isApproved && (
          <Alert severity="success" sx={{ mb: 3 }}>
            This BSC has been approved. All fields are now read-only.
          </Alert>
        )}

        {/* Perspective Weight Validation */}
        {!isWeightValid && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Warning:</strong> Total perspective weights must equal 100%. 
            Current total: <strong>{totalPerspectiveWeight}%</strong>
          </Alert>
        )}

        {/* Score Summary */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Perspective Scores</Typography>
              {calculatedData.perspectives.map((perspective, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{perspective.name}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {(perspective.perspectiveTotal || 0).toFixed(2)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((perspective.perspectiveTotal || 0) / perspective.weight * 100, 100)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: getGradeFromScore(effectiveScore)?.colorCode || '#f5f5f5',
                  color: 'white',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                }}
              >
                <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
                  {effectiveGrade}
                </Typography>
                <Typography variant="h4">
                  {effectiveScore.toFixed(1)}%
                </Typography>
                {managerAdjustedScore !== undefined && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                    Computed: {calculatedData.totalWeightedScore.toFixed(1)}% (Manager adjusted)
                  </Typography>
                )}
                {!managerAdjustedScore && calculatedData.rawTotalWeightedScore && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                    Raw: {calculatedData.rawTotalWeightedScore.toFixed(1)}% (capped at 110%)
                  </Typography>
                )}
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {getGradeFromScore(effectiveScore)?.description}
                </Typography>
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }} />
                <Typography variant="h6">
                  Bonus Entitlement: {effectiveBonus}%
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Manager Score Override */}
        {!isApproved && (
          <Paper sx={{ p: 3, mb: 3, border: '2px solid #f57c00' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#f57c00' }}>
              📊 Manager Score Override
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can override the computed total score. The new score will affect the final grade and bonus entitlement.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Adjusted Total Score (%)"
                  type="number"
                  fullWidth
                  value={managerAdjustedScore ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                    setManagerAdjustedScore(value);
                    setHasChanges(true);
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText={managerAdjustedScore !== undefined 
                    ? `Grade: ${effectiveGrade} | Bonus: ${effectiveBonus}%`
                    : 'Leave blank to use computed score'}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Adjustment Notes (required if overriding)"
                  fullWidth
                  multiline
                  rows={2}
                  value={managerAdjustmentNotes}
                  onChange={(e) => {
                    setManagerAdjustmentNotes(e.target.value);
                    setHasChanges(true);
                  }}
                  placeholder="Explain the reason for adjusting the score..."
                  error={managerAdjustedScore !== undefined && !managerAdjustmentNotes.trim()}
                  helperText={managerAdjustedScore !== undefined && !managerAdjustmentNotes.trim() 
                    ? 'Notes required when overriding score' : ''}
                />
              </Grid>
              {managerAdjustedScore !== undefined && (
                <Grid item xs={12}>
                  <Button 
                    variant="outlined" 
                    color="warning"
                    onClick={() => {
                      setManagerAdjustedScore(undefined);
                      setManagerAdjustmentNotes('');
                      setHasChanges(true);
                    }}
                  >
                    Clear Override (Use Computed Score)
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Show adjustment info if approved with override */}
        {isApproved && bscData.managerAdjustedScore !== undefined && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#fff3e0', border: '2px solid #f57c00' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#f57c00' }}>
              📊 Manager Override Applied
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Computed Score</Typography>
                <Typography variant="h6">{calculatedData.totalWeightedScore.toFixed(1)}%</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Adjusted Score</Typography>
                <Typography variant="h6" sx={{ color: '#f57c00' }}>{bscData.managerAdjustedScore}%</Typography>
              </Grid>
              {bscData.managerAdjustmentNotes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Adjustment Notes</Typography>
                  <Typography>{bscData.managerAdjustmentNotes}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Perspectives with KRAs */}
        {calculatedData.perspectives.map((perspective, index) => (
          <PerspectiveSection
            key={index}
            perspective={perspective}
            perspectiveIndex={index}
            onUpdatePerspective={handleUpdatePerspective}
            disabled={isApproved}
          />
        ))}

        {/* Employee Comments */}
        {bscData.employeeComments && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>Employee Comments</Typography>
            <Typography>{bscData.employeeComments}</Typography>
          </Paper>
        )}

        {/* Manager Comments */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Manager Comments</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={managerComments}
            onChange={(e) => {
              setManagerComments(e.target.value);
              setHasChanges(true);
            }}
            placeholder="Add your comments about this BSC submission..."
            disabled={isApproved}
          />
        </Paper>

        {/* Action Buttons (Bottom) */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSaveChanges}
            disabled={!hasChanges || isUpdating}
          >
            Save Changes
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => setShowRejectDialog(true)}
            disabled={bscData.status === 'approved' || isRejecting}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => setShowApproveDialog(true)}
            disabled={bscData.status === 'approved' || isApproving}
          >
            Approve
          </Button>
        </Box>

        {/* Approve Dialog */}
        <Dialog open={showApproveDialog} onClose={() => setShowApproveDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Approve BSC</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              You are about to approve the BSC submission for <strong>{employeeName}</strong> for period <strong>{bscData.reviewPeriod}</strong>.
            </Alert>
            <Typography variant="body2" gutterBottom>
              Final Score: <strong>{calculatedData.totalWeightedScore.toFixed(1)}%</strong>
              {calculatedData.rawTotalWeightedScore && (
                <span style={{ opacity: 0.7 }}> (raw: {calculatedData.rawTotalWeightedScore.toFixed(1)}%, capped at 110%)</span>
              )}
              {' | '}Grade: <strong>{calculatedData.finalGrade}</strong> | 
              Bonus: <strong>{getBonusFromScore(calculatedData.totalWeightedScore)}%</strong>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Manager Comments (Optional)"
              value={managerComments}
              onChange={(e) => setManagerComments(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowApproveDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleApprove}
              disabled={isApproving}
              startIcon={<CheckCircleIcon />}
            >
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reject BSC</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You are about to reject the BSC submission for <strong>{employeeName}</strong>. Please provide a reason.
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason for Rejection (Required)"
              value={managerComments}
              onChange={(e) => setManagerComments(e.target.value)}
              required
              error={!managerComments.trim()}
              helperText={!managerComments.trim() ? 'Please provide a reason' : ''}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={isRejecting || !managerComments.trim()}
              startIcon={<CancelIcon />}
            >
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageWithHeader>
  );
};

export default BSCManagerReview;
